import { VideoStreamSupportedMediaType } from '../types';

export abstract class VideoStreamController {
    /**
     * Enum identifier to distinguish subclass
     */
    abstract readonly type: VideoStreamSupportedMediaType;

    /**
     * The HTMLElement that holds the stream content.  Public so the widget can append it to itself
     * Some types include HTMLDivElement, HTMLImageElement, HTMLVideoElement and more
     */
    abstract streamElement: HTMLElement;

    protected _isInitialized: boolean;

    /**
     * A boolean to track internally if the stream is currently playing
     */
    protected _isPlaying: boolean;

    protected constructor() {
        this._isInitialized = false;

        this._isPlaying = false;
    }

    /**
     * A readonly getter for the widget to determine if the controller had been initialized
     */
    get isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * A readonly getter for the widget to determine if the controller is playing a stream
     */
    get isPlaying(): boolean {
        return this._isPlaying;
    }

    /**
     * VideoStreamControllers are initialized during the
     * {@link VideoStreamWidget.getReadyForDisplay} Widget lifecycle hook
     * @see {@link Widget.getReadyForDisplay}
     */
    initialize(): Promise<void> {
        this._isInitialized = true;
        return Promise.resolve();
    }

    /**
     * Queried by VideoStreamWidget to set {@link VideoStreamWidget.hasContentToPlay}
     */
    abstract canBePlayed(): Promise<boolean>;

    /**
     * Must be implemented by the subclass, resolves a promise when the video stream
     * starts playing successfully. Any thrown errors will have their messages displayed
     * in the DOM.
     */
    abstract play(): Promise<void>;

    /**
     * Pauses the stream.  Most controllers will override this function.  Ensure overrides either
     * call `super.pause()` or set `_isPlaying` themselves.
     */
    async pause(): Promise<void> {
        this._isPlaying = false;
    }

    /**
     * Stops the stream.  Sets _isInitialized to false.
     */
    async stop(): Promise<void> {
        this._isInitialized = false;
    }
}
